import { Attachment, ModelWithAttachments } from './attachment';
import { type Invoice } from './invoice';
import { DisputeItem } from './dispute_item';
import { Partner } from './partner';
import { Call } from './call';
import { Email } from './email';
import { Task } from './task';
import { DisputeReason } from './dispute_reason';
import { User } from './user';
import { Type } from 'class-transformer';
import type dayjs from 'dayjs';
import { TransformStringToDayJS } from './utils';
import { Priority } from '@stuut/common/src/constants';

export enum DisputeStatus {
  PROPOSED = 'proposed',
  PROCESS = 'in_process',
  REVIEW = 'needs_review',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

export const DisputeStatusTextMap = {
  [DisputeStatus.PROPOSED]: 'Draft',
  [DisputeStatus.PROCESS]: 'In Process',
  [DisputeStatus.REVIEW]: 'Needs Review',
  [DisputeStatus.RESOLVED]: 'Resolved',
  [DisputeStatus.CLOSED]: 'Closed',
};

export const DisputeStatusLabels = Object.values(DisputeStatus).map(
  (status) => DisputeStatusTextMap[status] || status,
);

export const DisputeStatusOptions = Object.values(DisputeStatus).map((value) => ({
  value,
  label: DisputeStatusTextMap[value] || value,
}));

export const DisputePriorityOptions = Object.values(Priority).map((value) => ({
  value,
  label: value,
}));

export class Dispute extends ModelWithAttachments {
  @Type(() => User)
  assignee: User;

  @Type(() => Partner)
  partner: Partner;

  @Type(() => DisputeReason)
  reason: DisputeReason;

  @Type(() => Email)
  email: Email;

  @Type(() => DisputeItem)
  items: DisputeItem[];

  @Type(() => Call)
  call: Call;

  @Type(() => Task)
  tasks: Task[];

  @TransformStringToDayJS()
  approved_at: dayjs.Dayjs;

  amount: number;

  assignee_id: string;

  call_id: string;

  @Type(() => Task)
  related_dispute_tasks: Task[];

  closed_at: string;

  custom_fields: string;

  custom_object_type: string;

  deduction_id: string;

  description: string;

  number: string;

  email_id: string;

  external_status: string;

  is_originated_in_stuut: string;

  is_resolved_in_stuut: string;

  @TransformStringToDayJS()
  last_synced_at: dayjs.Dayjs;

  partner_id: string;

  priority: Priority;

  reason_id: string;

  resolution: string;

  @TransformStringToDayJS()
  rejected_at: dayjs.Dayjs;

  @TransformStringToDayJS()
  remote_created_at: dayjs.Dayjs;

  remote_id: string;

  remote_metadata: string;

  @TransformStringToDayJS()
  remote_modified_at: dayjs.Dayjs;

  @TransformStringToDayJS()
  resolution_received_at: dayjs.Dayjs;

  resolution_summary: string;

  sms_id: string;

  status: DisputeStatus;

  sync_status: string;

  total_item_amount: number;

  get disputeItem(): DisputeItem | undefined {
    return this.items?.[0];
  }

  get disputeItemId(): string | undefined {
    return this.disputeItem?.id;
  }

  get itemAmount(): number | undefined {
    return this.disputeItem?.amount;
  }

  get currency(): string | undefined {
    return this.disputeItem?.currency;
  }

  get invoice(): Invoice | undefined {
    return this.disputeItem?.invoice;
  }

  get invoice_id(): string | undefined {
    return this.disputeItem?.invoice_id;
  }

  get isActive(): boolean {
    return this.status === DisputeStatus.PROCESS || this.status === DisputeStatus.REVIEW;
  }

  get isClosed(): boolean {
    return this.status === DisputeStatus.CLOSED;
  }

  get isNeedsReview(): boolean {
    return this.status === DisputeStatus.REVIEW;
  }

  get isProposed(): boolean {
    return this.status === DisputeStatus.PROPOSED;
  }

  get isResolved(): boolean {
    return this.status === DisputeStatus.RESOLVED;
  }

  get isRequiresActivate(): boolean {
    return Boolean(this.isProposed && this.openActivateTask);
  }

  get isTerminal(): boolean {
    return this.status === DisputeStatus.RESOLVED || this.status === DisputeStatus.CLOSED;
  }

  get attachmentIds(): string[] | [] {
    return (this.attachments || []).map((att) => att.id);
  }

  get statusText(): string {
    return DisputeStatusTextMap[this.status] || this.status;
  }

  get createTask(): Task | undefined {
    return this.tasks?.find((task) => task.isDisputeCreate);
  }

  get activateTask(): Task | undefined {
    return this.tasks?.find((task) => task.isDisputeActivate);
  }

  get openCreateTask(): Task | undefined {
    return this.tasks?.find((task) => task.isDisputeCreate && task.isOpen);
  }

  get openActivateTask(): Task | undefined {
    return this.tasks?.find((task) => task.isDisputeActivate && task.isOpen);
  }

  get updateTasks(): Task[] | undefined {
    return this.tasks?.filter((task) => task.isDisputeUpdate);
  }

  get openUpdateTasks(): Task[] | undefined {
    return this.tasks?.filter((task) => task.isOpen && task.isDisputeUpdate);
  }

  get source(): string | undefined {
    return this.sourceTask?.source;
  }

  get sourceTask(): Task | undefined {
    return this.tasks?.find((t) => !t?.isArchived && (t?.isDisputeActivate || t?.isDisputeCreate));
  }

  get callId() {
    return this.call_id;
  }

  get emailId() {
    return this.email_id;
  }
}
